/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  HStack,
  Text,
  VStack,
  Circle,
  IconButton,
  Box,
  FormErrorMessage,
  Textarea,
} from '@chakra-ui/react';
import { Controller, UseFormSetValue, useWatch, Path } from 'react-hook-form';
import DeleteIcon from '@/assets/svg/delete.svg?react';
import { useDisclosure } from '@chakra-ui/react';
import CustomSelect from '../common/CustomSelect';
import Asterisk from '../common/Asterisk';
import { useGetColors } from '@/services/addProduct';
import { ColorOption } from '@/services/addProduct/types';
import ImageUpload from '../common/ImageUpload';
import { type FormData } from '@/services/addProduct/types';
import CustomConfirmationModal from '@/components/common/ConfirmationModal';
import ConfirmDeleteIcon from '@/assets/svg/delete-bg.svg?react';
import { useMemo } from 'react';

interface AddOnVariantItemProps {
  control: any;
  errors: any;
  addOnIndex: number;
  variantIndex: number;
  removeVariant: () => void;
  currencySymbol: string;
  setValue: UseFormSetValue<FormData>;
}

const AddOnVariantItem = ({
  control,
  errors,
  addOnIndex,
  variantIndex,
  removeVariant,
  currencySymbol,
  setValue,
}: AddOnVariantItemProps) => {
  const { data: colors = [] } = useGetColors();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const fieldPrefix = `add_ons_data.${addOnIndex}.variants.${variantIndex}` as const;

  const watchedValues = useWatch({
    control,
    name: [`add_ons_data.${addOnIndex}.variants`, `${fieldPrefix}.photo`],
  });

  const variantsInGroup = watchedValues?.[0] || [];
  const currentPhoto = watchedValues?.[1];

  const isOnlyVariant = variantsInGroup.length === 1;

  const colorOptions = useMemo(
    () =>
      colors.map((color: ColorOption) => ({
        id: String(color.id),
        label: (
          <Flex align="center" gap={2}>
            <Circle size="12px" bg={color.hex_code} />
            <Text>{color.name}</Text>
          </Flex>
        ),
        searchLabel: color.name,
      })),
    [colors],
  );

  const handleImageChange = (file: File | null) => {
    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setValue(`${fieldPrefix}.photo` as Path<FormData>, blobUrl);
      setValue(`${fieldPrefix}.photoFile` as Path<FormData>, file);
      setValue(`${fieldPrefix}.isModified` as Path<FormData>, true);
    } else {
      setValue(`${fieldPrefix}.photo` as Path<FormData>, '');
      setValue(`${fieldPrefix}.photoFile` as Path<FormData>, null);
    }
  };

  const handleDeleteClick = () => {
    if (isOnlyVariant) return;
    onOpen();
  };

  const handleConfirmDelete = () => {
    removeVariant();
    onClose();
  };

  return (
    <>
      <Box
        border="1px"
        borderColor="border.default60"
        borderRadius="md"
        p={4}
        bg="white"
        mt={4}
        boxShadow="sm"
      >
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontWeight="medium" fontSize="md">
            Add-on Variant {variantIndex + 1}
          </Text>

          <IconButton
            aria-label="Delete variant"
            icon={<DeleteIcon />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={handleDeleteClick}
            isDisabled={isOnlyVariant}
            opacity={isOnlyVariant ? 0.4 : 1}
            cursor={isOnlyVariant ? 'not-allowed' : 'pointer'}
          />
        </Flex>

        <VStack spacing={4} align="stretch">
          <HStack
            spacing={4}
            align="start"
            w="full"
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
            gap={4}
          >
            <FormControl
              isInvalid={!!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.color}
              flexBasis={{ base: '100%', md: '200px' }}
              flexGrow={1}
            >
              <FormLabel fontSize="sm">
                Color <Asterisk />
              </FormLabel>
              <Controller
                name={`${fieldPrefix}.color`}
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    options={colorOptions}
                    selectedValue={field.value ? String(field.value.id) : null}
                    onChange={(id: string) => {
                      const selectedColor = colors.find((c: ColorOption) => String(c.id) === id);
                      field.onChange(selectedColor || null);
                      setValue(`${fieldPrefix}.isModified` as Path<FormData>, true);
                    }}
                    placeholder="Select color"
                    isSearchable={true}
                    isFullWidth={true}
                    size="md"
                  />
                )}
              />
              <FormErrorMessage>
                {errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.color?.message ||
                  errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.color?.name
                    ?.message ||
                  errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.color?.hex_code
                    ?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Display Name */}
            <FormControl
              isInvalid={
                !!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.display_name
              }
            >
              <FormLabel fontSize="sm">
                Display Name <Asterisk />
              </FormLabel>
              <Controller
                name={`${fieldPrefix}.display_name`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder="Enter display name"
                    size="md"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValue(`${fieldPrefix}.isModified` as Path<FormData>, true);
                    }}
                  />
                )}
              />
              <FormErrorMessage>
                {
                  errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.display_name
                    ?.message
                }
              </FormErrorMessage>
            </FormControl>

            {/* Price */}
            <FormControl
              isInvalid={!!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.price}
            >
              <FormLabel fontSize="sm">
                Price <Asterisk />
              </FormLabel>
              <Box position="relative">
                <Text
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  fontSize="sm"
                  color="gray.500"
                  pointerEvents="none"
                  zIndex="1"
                >
                  {currencySymbol}
                </Text>
                <Controller
                  name={`${fieldPrefix}.price`}
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="text"
                      placeholder="0.00"
                      pl="40px"
                      size="md"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                          field.onChange(val);
                          setValue(`${fieldPrefix}.isModified` as Path<FormData>, true);
                        }
                      }}
                      onFocus={() => {
                        if (field.value === '0.00') {
                          field.onChange('');
                        }
                      }}
                    />
                  )}
                />
              </Box>
              <FormErrorMessage>
                {errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.price?.message}
              </FormErrorMessage>
            </FormControl>

            {/* Weight */}
            <FormControl
              isInvalid={!!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.weight}
            >
              <FormLabel fontSize="sm">
                Weight (kg) <Asterisk />
              </FormLabel>
              <Controller
                name={`${fieldPrefix}.weight`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="text"
                    placeholder="0.00"
                    size="md"
                    value={field.value ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                        field.onChange(val);
                        setValue(`${fieldPrefix}.isModified` as Path<FormData>, true);
                      }
                    }}
                    onFocus={() => {
                      if (field.value === '0.00') {
                        field.onChange('');
                      }
                    }}
                  />
                )}
              />
              <FormErrorMessage>
                {errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.weight?.message}
              </FormErrorMessage>
            </FormControl>
          </HStack>

          <HStack spacing={6} align="start" w="full">
            <FormControl
              isInvalid={
                !!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.photoFile ||
                !!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.photo ||
                !!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]
              }
              flex={1}
            >
              <FormLabel fontSize="sm">
                Photo <Asterisk />
              </FormLabel>
              <ImageUpload
                htmlFor={`addon-photo-${addOnIndex}-${variantIndex}`}
                error={errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.photoFile}
                onChange={handleImageChange}
                initialImage={typeof currentPhoto === 'string' ? currentPhoto : null}
                required
              />
            </FormControl>

            <FormControl
              isInvalid={
                !!errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.description
              }
              flex={1}
            >
              <FormLabel fontSize="sm">
                Description <Asterisk />
              </FormLabel>
              <Controller
                name={`${fieldPrefix}.description`}
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter variant description (e.g., material, features, etc.)"
                    size="md"
                    h="105px"
                    rows={5}
                    resize="vertical"
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setValue(`${fieldPrefix}.isModified` as Path<FormData>, true);
                    }}
                  />
                )}
              />
              <FormErrorMessage>
                {errors?.add_ons_data?.[addOnIndex]?.variants?.[variantIndex]?.description?.message}
              </FormErrorMessage>
            </FormControl>
          </HStack>
        </VStack>
      </Box>

      {/* Confirmation Modal */}
      <CustomConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleConfirmDelete}
        icon={ConfirmDeleteIcon}
        title="Delete Add-on Variant?"
        description="Are you sure you want to delete this add-on variant? This action cannot be undone."
        buttonText="Delete"
      />
    </>
  );
};

export default AddOnVariantItem;
